import { useState, useEffect } from 'react';
import { readCookie, writeCookie, onSettingsMayHaveChanged } from '../utils/cookies';

const COOKIE_KEY = 'ukwa-theme';
export const DEFAULT_THEME = 'light';
// Legacy key — previous builds persisted the theme in localStorage.
const LEGACY_KEY = 'ukwa-theme';

// Resolve the starting theme in priority order:
//   1. the saved cookie, 2. a legacy localStorage value (migrated on mount),
//   3. light (the default). The operating-system colour-scheme preference is
//   intentionally not consulted — light is the default until the user picks a
//   theme in the accessibility panel.
function getInitialTheme() {
  const fromCookie = readCookie(COOKIE_KEY);
  if (fromCookie === 'light' || fromCookie === 'dark') return fromCookie;

  try {
    const ls = localStorage.getItem(LEGACY_KEY);
    if (ls === 'light' || ls === 'dark') return ls;
  } catch {
    // localStorage unavailable
  }

  return DEFAULT_THEME;
}

export function useTheme() {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    writeCookie(COOKIE_KEY, theme);
    // Drop any stale localStorage value now that the cookie is authoritative.
    try { localStorage.removeItem(LEGACY_KEY); } catch { /* ignore */ }
  }, [theme]);

  // The cookie is the record of the user's choice (see useAccessibility).
  useEffect(() => onSettingsMayHaveChanged(() => {
    const saved = readCookie(COOKIE_KEY);
    if (saved === 'light' || saved === 'dark') setThemeState(saved);
  }), []);

  const toggleTheme = () => setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  const setTheme = (next) => setThemeState(next === 'dark' ? 'dark' : 'light');

  return { theme, toggleTheme, setTheme };
}
